/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-floating-promises */
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Card,
  Fab,
  Grid,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map, MapEvent } from "ol";
import { unByKey } from "ol/Observable";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { classNames, If } from "react-extras";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useSize } from "react-use";
import {
  AccessEnum,
  BerthCreateDto,
  DamCreateDto,
  useBerthApi,
  useDamApi,
  useEventApi,
  useFairwayApi,
  useFerryApi,
  useFleetApi,
  useNauticalWarningApi,
  usePortApi,
  usePortServiceApi,
  useReimariApi,
  useSeaSurveyApi,
  useThirdPartyApi,
} from "~/api/GeneratedApi";
import iceLegend from "~/assets/img/iceLegend.png";
import { setEvents, setPorts } from "~/redux/reducers/dataReducer";
import {
  setAllFleets,
  setSelectedMapFleet,
} from "~/redux/reducers/fleetReducer";
import { setMapType } from "~/redux/reducers/mapReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { flattenMapLayers } from "~/utils/flattenMapLayers";
import { useAccess } from "~/utils/hooks/useAccess";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import DamDialog from "../dam-page/dialogs/DamDialog";
import FerryDialog from "../ferry-page/FerryDialog";
import { AISFleetItemDto } from "../fleets-page/types";
import { ArrivalZoneDialog } from "../PortsPage/dialogs/ArrivalZoneDialog";
import BerthDialog from "../PortsPage/dialogs/BerthDialog";
import { ServiceDialog } from "../PortsPage/dialogs/ServiceDialog";
import { useArrivalZones } from "../PortsPage/hooks/useArrivalZones";
import CesiumMap from "./3d/CesiumMap";
import CesiumMapButton from "./components/CesiumMapButton";
import EventDialog from "./components/events/EventDialog";
import MapDrawer from "./components/MapDrawer";
import MapSearch from "./components/MapSearch";
import MapToolbar, { ToolbarAction } from "./components/MapToolbar";
import MapPortCallsDrawer from "./components/ports/MapPortCallsDrawer";
import PortDialog from "./components/ports/PortDialog";
import PortFocusBar from "./components/ports/PortFocusBar";
import { addAISVesselsToMap } from "./functions/addAISVesselsToMap";
import {
  addArrivalZonesToMap,
  removeArrivalZones,
} from "./functions/addArrivalZonesToMap";
import { addAtoNsToMap } from "./functions/addAtoNsToMap";
import {
  addBerthsToMap,
  removeAllBerthsFromMap,
} from "./functions/addBerthsToMap";
import { addDamsToMap } from "./functions/addDamsToMap";
import { addEventsToMap } from "./functions/addEventsToMap";
import { addFairwaysToMap } from "./functions/addFairwaysToMap";
import { addFerriesToMap } from "./functions/addFerriesToMap";
import { addLuodeToMap } from "./functions/addLuodeToMap";
import { addMeasurementsToMap } from "./functions/addMeasurementsToMap";
import { addNauticalWarningsToMap } from "./functions/addNauticalWarningsToMap";
import {
  addPortServicesToMap,
  removeAllPortServicesFromMap,
} from "./functions/addPortServicesToMap";
import { addPortsToMap } from "./functions/addPortsToMap";
import { atonFaultHandler } from "./functions/atonFaultHandler";
import { aisVesselToMapItem } from "./functions/dataConverters";
import { findLayerByTitle } from "./functions/findLayerByTitle";
import { findSourceByTitle } from "./functions/findSourceByTitle";
import {
  readMapSettings,
  updateMapSettings,
} from "./functions/localMapSettings";
import { readMapPosition, saveMapPosition } from "./functions/mapPositionStore";
import { zoomOnFeature } from "./functions/zoomOnFeature";
import useAISVessels from "./hooks/useAISVessels";
import useCreateMap from "./hooks/useCreateMap";
import useMapPopup from "./hooks/useMapPopup";
import { usePortFocus } from "./hooks/usePortFocus";
import { useSearchItemConverters } from "./hooks/useSearchItemConverters";
import { useSeaSurveyEventHandler } from "./hooks/useSeaSurveyEventHandler";
import { useVehicleEquipmentsOnMap } from "./hooks/useVehicleEquipmentsOnMap";
import MapPopups from "./popups/MapPopups";
import { LayerTitle } from "./types/layers";
import { CustomMapEventTypes } from "./types/map";
import { SearchListItem } from "./types/search";

export interface MapLocationState {
  featureId?: string;
}

export const useMapStyles = makeStyles((theme) => ({
  mapContainer: {
    height: "100%",
    position: "relative",
  },
  popup: {
    position: "absolute",
    bottom: 12,
    left: -50,
    "&:after": {
      border: "solid transparent",
      content: '""',
      height: 0,
      width: 0,
      position: "fixed",
      pointerEvents: "none",
      borderTopColor: theme.palette.background.paper,
      borderWidth: 10,
      left: 0,
      marginLeft: -10,
    },
    "& .MuiCardHeader-content": {
      maxWidth: "min(400px, 60vw)",
    },
  },
  settingsButton: {
    position: "absolute",

    right: "0.5em",
    zIndex: theme.zIndex.appBar,
    [theme.breakpoints.down("md")]: {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
    },
    [theme.breakpoints.up("md")]: {
      top: "0.5em",
    },
  },
  portInFocus: {
    [theme.breakpoints.down("md")]: {
      bottom: `calc(${theme.spacing(2)} + ${52}px)`, // Snackbar 52px
    },
  },
}));

const MapPage = (): JSX.Element => {
  const classes = useMapStyles();
  const { t } = useTranslation("map");
  const { createMap } = useCreateMap();
  const theme = useTheme();
  const isMediumOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));
  const iceLevelLayerVisible = readMapSettings(
    "layers",
    LayerTitle.IceLevel,
  )?.visible;
  const showIceLevel = readMapSettings("iceLegend");
  const [mapDrawerOpen, setMapDrawerOpen] = useState(readMapSettings("open"));
  const [mapCreated, setMapCreated] = useState(false);
  const [pinned, setPinned] = useState(readMapSettings("pinned"));
  const [featuresAdded, setFeaturesAdded] = useState(false);
  const [searchItems, setSearchItems] = useState<SearchListItem[]>([]);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [activeAction, setActiveAction] = useState<ToolbarAction>();
  const [atonsAdded, setAtonsAdded] = useState(false);
  const [onlyFaultedAtons, setOnlyFaultedAtons] = useState(
    readMapSettings("atonOnlyFaulted"),
  );
  const [onlyRemoteAtons, setOnlyRemoteAtons] = useState(
    readMapSettings("atonOnlyRemote"),
  );
  const [surveyOutlines, setSurveyOutlines] = useState(
    readMapSettings("surveyOutlines"),
  );
  const mapRef = useRef<Map>();
  // usePTOPCarsOnMap(mapRef.current, mapCreated);
  // usePTOPTrailersOnMap(mapRef.current, mapCreated);
  useVehicleEquipmentsOnMap(mapRef.current, mapCreated);
  useSeaSurveyEventHandler(mapRef.current);

  const { state: locationState } = useLocation<MapLocationState>();
  const history = useHistory<MapLocationState>();
  const dispatch = useAppDispatch();
  const { fetchVesselData } = useAISVessels();
  const { portToSearchItem, vesselToSearchItem, atonToSearchItem } =
    useSearchItemConverters();
  const {
    createPopup,
    hoveredItem,
    selectedItem,
    multipleItems,
    unmountPopup,
  } = useMapPopup();
  const { portFocus, focusedPort, handleFocusRemove, viewChanged, refocus } =
    usePortFocus({
      map: mapRef.current,
    });
  const selectedMapFleetId = useAppSelector((s) => s.fleet.selectedMapFleetId);
  const selectedMapFleet = useAppSelector((s) => s.fleet.selectedMapFleet);
  const vessels = useAppSelector((s) => s.data.vessels);
  const mapType = useAppSelector((s) => s.map.mapType);
  const cesiumState = useAppSelector((x) => x.map.cesiumState);

  const portApi = usePortApi();
  const eventApi = useEventApi();
  const fairwayApi = useFairwayApi();
  const berthApi = useBerthApi();
  const portServiceApi = usePortServiceApi();
  const { getFleet, getFleets } = useFleetApi();
  const { getSeaSurveys } = useSeaSurveyApi();
  const { getDams, addDam } = useDamApi();
  const { getFerries, addFerry } = useFerryApi();
  const { getAtonUnits } = useReimariApi();
  const { getLuode } = useThirdPartyApi();
  const { SeaSurveyRead } = useAccess();
  const { getNauticalWarnings } = useNauticalWarningApi();

  const { data: measurements } = useCustomQuery(
    "sea-surveys",
    () => getSeaSurveys(),
    {
      enabled: mapType === "2d" && mapCreated,
      onSuccess: (s) => {
        if (mapRef.current) {
          addMeasurementsToMap(mapRef.current, s?.results);
        }
      },
      requiredAccess: AccessEnum.SeaSurveyRead,
    },
  );

  const { refetch: getPorts, data: ports } = useCustomQuery(
    "ports",
    () => portApi.getPorts(),
    {
      enabled: mapType === "2d" && mapCreated,
      onSuccess: (p) => {
        // Update to redux
        dispatch(setPorts(p.results));
        if (mapRef.current) {
          addPortsToMap(mapRef.current, p.results);
        }
      },
      requiredAccess: AccessEnum.PortRead,
    },
  );

  const { refetch: getEvents } = useCustomQuery(
    "events",
    () => eventApi.getEvents(),
    {
      enabled: mapType === "2d" && mapCreated,
      onSuccess: (d) => {
        // Update store
        dispatch(setEvents(d.results));
        if (mapRef.current) {
          addEventsToMap(mapRef.current, d.results);
        }
      },
      requiredAccess: AccessEnum.EventRead,
    },
  );
  useCustomQuery("nautical-warnings", () => getNauticalWarnings(), {
    enabled: mapType === "2d" && mapCreated,
    onSuccess: (d) => {
      if (mapRef.current) {
        addNauticalWarningsToMap(mapRef.current, d.results);
      }
    },
    requiredAccess: AccessEnum.EventRead,
  });

  const { refetch: refetchDams } = useCustomQuery("dams", () => getDams(), {
    enabled: mapType === "2d" && mapCreated,
    onSuccess: (d) => {
      if (mapRef.current) {
        addDamsToMap(mapRef.current, d.results);
      }
    },
    requiredAccess: AccessEnum.DamRead,
  });

  useCustomQuery("luode", () => getLuode(), {
    enabled: mapType === "2d" && mapCreated,
    onSuccess: (d) => {
      if (mapRef.current) {
        addLuodeToMap(mapRef.current, d);
      }
    },
  });

  React.useEffect(() => {
    if (!mapRef.current) return;
    removeAllBerthsFromMap(mapRef.current);
    removeAllPortServicesFromMap(mapRef.current);
    removeArrivalZones(mapRef.current);
  }, [focusedPort]);

  const { refetch: refetchBerths } = useCustomQuery(
    { key: "berths-for-port", id: focusedPort?.id },
    () => berthApi.getBerths({ id: focusedPort?.id }),
    {
      enabled: mapType === "2d" && mapCreated && !!focusedPort,
      onSuccess: (data) => {
        if (!mapRef.current) return;
        addBerthsToMap(mapRef.current, data.results);
      },
      requiredAccess: AccessEnum.PortRead,
    },
  );
  const { arrivalZonesForPort: arrivalZones, refectArrivalZonesForPort } =
    useArrivalZones({
      selectedPortId: focusedPort?.id,
    });

  React.useEffect(() => {
    if (
      arrivalZones &&
      mapRef.current &&
      mapType === "2d" &&
      mapCreated &&
      !!focusedPort
    ) {
      addArrivalZonesToMap(mapRef.current, arrivalZones);
    }
  }, [arrivalZones, focusedPort, mapCreated, mapType, t]);

  const { refetch: refetchPortServices } = useCustomQuery(
    { key: "services-for-port", id: focusedPort?.id },
    () => portServiceApi.getPortServices({ id: focusedPort?.id }),
    {
      enabled: mapType === "2d" && mapCreated && !!focusedPort,
      onSuccess: (data) => {
        if (!mapRef.current) return;
        addPortServicesToMap(mapRef.current, data.results);
      },
      requiredAccess: AccessEnum.PortRead,
    },
  );

  const { data: atons } = useCustomQuery("aton-units", () => getAtonUnits(), {
    enabled: mapType === "2d" && mapCreated,
    onSuccess: (d) => {
      if (mapRef.current) {
        addAtoNsToMap(mapRef.current, d.results);
        setAtonsAdded(true);
      }
    },
    refetchOnWindowFocus: false,
    requiredAccess: AccessEnum.SafetyDeviceRead,
  });

  const { refetch: refetchFerries } = useCustomQuery(
    "ferries",
    () => getFerries(),
    {
      enabled: mapType === "2d" && mapCreated,
      onSuccess: (d) => {
        if (mapRef.current) {
          addFerriesToMap(mapRef.current, d.results);
        }
      },
      requiredAccess: AccessEnum.FerryRead,
    },
  );

  const { mutate: addDamMutation } = useCustomMutation(
    (d: DamCreateDto) => addDam(d),
    {
      onSuccess: () => {
        refetchDams();
      },
    },
  );

  const { mutate: addFerryMutation } = useCustomMutation(
    (d: DamCreateDto) => addFerry(d),
    {
      onSuccess: () => {
        refetchFerries();
      },
    },
  );

  const { mutate: addBerthMutation } = useCustomMutation(
    (berth: BerthCreateDto) => berthApi.addBerth(berth),
    {
      onSuccess: () => {
        setActiveAction(undefined);
        refetchBerths();
      },
    },
  );

  // Get all fleets and update store
  useCustomQuery("fleets", () => getFleets(), {
    onSuccess: (d) => dispatch(setAllFleets(d.results)),
    enabled: mapType === "2d" && mapCreated,
  });

  useEffect(() => {
    if (!selectedMapFleetId && mapRef.current) {
      const source = findSourceByTitle(mapRef.current, LayerTitle.FleetAIS);
      source?.clear();
      dispatch(setSelectedMapFleet(null));
    }
  }, [dispatch, selectedMapFleetId]);

  useEffect(() => {
    if (!mapRef.current) return;
    const aisMapItems = selectedMapFleet?.fleetItems
      ?.filter(
        (x) =>
          (x as AISFleetItemDto).aisData && (x as AISFleetItemDto).aisData.mmsi,
      )
      .map((a) => aisVesselToMapItem((a as AISFleetItemDto).aisData));
    // Don't add vessels that belong to a selected fleet
    const filteredVessels = aisMapItems
      ? vessels?.filter((v) => !aisMapItems?.find((i) => i.mmsi === v.mmsi))
      : vessels;

    addAISVesselsToMap(mapRef.current, filteredVessels ?? []);
    if (aisMapItems) {
      // Add to a separate fleet layer
      addAISVesselsToMap(mapRef.current, aisMapItems, LayerTitle.FleetAIS);
    }
  }, [selectedMapFleet, vessels]);

  const { refetch: refetchFleet } = useCustomQuery(
    { key: "fleet", id: selectedMapFleetId },
    () => getFleet(selectedMapFleetId!),
    {
      enabled: mapType === "2d" && mapCreated && !!selectedMapFleetId,
      onSuccess: (d) => {
        dispatch(setSelectedMapFleet(d));
      },
    },
  );

  useEffect(() => {
    if (!selectedMapFleetId) return;
    refetchFleet();
  }, [refetchFleet, selectedMapFleetId]);

  useCustomQuery("fairways", () => fairwayApi.getFairways(), {
    enabled: mapType === "2d" && mapCreated,
    onSuccess: (d) => {
      if (mapRef.current) {
        addFairwaysToMap(mapRef.current, d.results);
      }
    },
  });

  const handlePin = useCallback((isMedium?: boolean) => {
    setPinned((prev) => {
      const val = isMedium ? false : !prev;
      updateMapSettings({ pinned: val });
      return val;
    });
  }, []);

  const handleDrawerToggle = (state: boolean) => {
    setMapDrawerOpen(state);
    updateMapSettings({ open: state });
  };

  useEffect(() => {
    if (!mapRef.current || !ports?.results) return;
    portFocus({
      ports: ports.results,
    });
  }, [ports, featuresAdded, portFocus, history]);

  // Listener for any types of features being added to the map
  useEffect(() => {
    const listener = () => {
      setFeaturesAdded((prev) => !prev);
    };
    if (mapCreated) {
      mapRef.current?.on(CustomMapEventTypes.FeaturesAdded, listener);
    }
    return () => {
      mapRef.current?.un(CustomMapEventTypes.FeaturesAdded, listener);
    };
  }, [mapCreated]);

  useEffect(() => {
    setSearchItems([
      ...(ports &&
      findLayerByTitle(mapRef.current, LayerTitle.Ports)?.getVisible()
        ? ports.results.map((p) => portToSearchItem(p))
        : []),
      ...(vessels &&
      findLayerByTitle(mapRef.current, LayerTitle.Vessels)?.getVisible()
        ? vessels.map((v) => vesselToSearchItem(v))
        : []),
      ...(atons &&
      findLayerByTitle(mapRef.current, LayerTitle.AtoNs)?.getVisible()
        ? atons.results.map((a) => atonToSearchItem(a))
        : []),
    ]);
  }, [
    portToSearchItem,
    ports,
    vesselToSearchItem,
    vessels,
    settingsChanged,
    atons,
    atonToSearchItem,
  ]);

  // AIS data fetching, fetched by interval
  // Check layer visibility
  useEffect(() => {
    if (!mapCreated) return;
    fetchVesselData(
      !!findLayerByTitle(mapRef.current, LayerTitle.Vessels)?.getVisible(),
    );
  }, [fetchVesselData, mapCreated, settingsChanged]);

  // Create container with useSize to monitor width changes
  const [mapContainer, { width: containerWidth, height: containerHeight }] =
    useSize(
      <Grid item xs className={classes.mapContainer} id="mapContainer">
        <MapSearch items={searchItems} map={mapRef.current} />

        <Card id="popup" className={classes.popup}>
          {mapRef.current && (
            <MapPopups
              map={mapRef.current}
              hoveredItem={hoveredItem}
              selectedItem={selectedItem}
              multipleItems={multipleItems}
            />
          )}
        </Card>

        {!pinned && (
          <Tooltip title={t("drawer-button").toString()}>
            <Fab
              color="primary"
              onClick={() => handleDrawerToggle(true)}
              className={classNames(classes.settingsButton, {
                [classes.portInFocus]: !!focusedPort,
              })}
            >
              <TuneIcon />
            </Fab>
          </Tooltip>
        )}
        <MapToolbar
          onActionClick={setActiveAction}
          portInFocus={!!focusedPort}
        />
        {focusedPort ? null : <CesiumMapButton />}
        <PortFocusBar
          port={focusedPort}
          onClose={handleFocusRemove}
          viewChanged={viewChanged}
          refocus={refocus}
        />
        {iceLevelLayerVisible && showIceLevel ? (
          <Box
            sx={{
              position: "absolute",
              bottom: 40,
              right: 0,
              zIndex: 10,
              transform: "scale(0.62)",
              transformOrigin: "bottom right",
            }}
          >
            <img
              // style={{ transform: "scale(0.5)" }}
              alt="Legend"
              src={iceLegend}
            />
          </Box>
        ) : null}
      </Grid>,
    );

  // Create map on mount
  useEffect(() => {
    mapRef.current = createMap({
      target: "mapContainer",
      existingMap: mapRef.current,
    });
    setMapCreated(true);

    return () => {
      // mapRef.current?.dispose();
      // mapRef.current = undefined;
      setMapCreated(false);
    };
  }, [createMap]);

  useEffect(() => {
    // Attach event listeners for popups
    if (mapRef.current) {
      createPopup({ map: mapRef.current });
    }

    return () => {
      unmountPopup();
    };
  }, [createPopup, unmountPopup]);

  // Resize map when container size changes
  useEffect(() => {
    mapRef.current?.updateSize();
    // Attributions are collapsed by default now

    // const controls = mapRef.current?.getControls()?.getArray();
    // controls?.forEach((c) => {
    //   if (c instanceof Attribution) {
    //     c.setCollapsed(isMediumOrSmaller);
    //   }
    // });
  }, [containerWidth, containerHeight, mapType, isMediumOrSmaller]);

  // Check the state of the location and focus on a feature
  useEffect(() => {
    if (!locationState?.featureId || !mapRef.current) return;
    if (zoomOnFeature(mapRef.current, locationState.featureId, 18)) {
      dispatch(setMapType("2d"));
      // Remove the state from the location
      history.push({
        state: undefined,
      });
      saveMapPosition(mapRef.current);
    }
  }, [locationState, history, featuresAdded, dispatch]);

  // Keep the map center and zoom on session storage
  useEffect(() => {
    const listener = (e: MapEvent) => {
      saveMapPosition(e.map);
    };
    if (!locationState?.featureId) {
      const view = mapRef.current?.getView();
      const storedPos = readMapPosition();
      if (storedPos && storedPos.zoom && Array.isArray(storedPos.center)) {
        view?.setCenter(storedPos.center);
        view?.setZoom(storedPos.zoom);
      }
      const listeners = mapRef.current?.getListeners("moveend");
      if (!listeners?.find((l) => l === listener)) {
        mapRef.current?.on("moveend", listener);
      }
    }
    return () => {
      mapRef.current?.un("moveend", listener);
    };
  }, [locationState?.featureId]);

  // Map settings event handler
  useEffect(() => {
    const handleSettingsChange = () => {
      if (!mapRef.current) return;
      setSettingsChanged((old) => !old);
      const allLayers = flattenMapLayers(mapRef.current);
      allLayers.forEach((l) => {
        updateMapSettings({
          layers: {
            title: l.get("title"),
            group: l.get("groupTitle"),
            zIndex: l.getZIndex(),
            visible: l.getVisible(),
          },
        });
      });
      mapRef.current.render();
    };

    handleSettingsChange();
    const key = mapRef.current?.on(
      CustomMapEventTypes.SettingsChanged,
      handleSettingsChange,
    );
    return () => {
      if (key) unByKey(key);
    };
  }, []);

  // Aton fault indicator event handler
  useEffect(() => {
    const enabled = readMapSettings("atonAlertCircle");
    if (mapRef.current && atonsAdded) atonFaultHandler(mapRef.current, enabled);
    mapRef.current?.render();
  }, [
    mapCreated,
    settingsChanged,
    atonsAdded,
    onlyFaultedAtons,
    onlyRemoteAtons,
  ]);

  // Update atons if layer settings are changed
  useEffect(() => {
    if (
      !mapRef.current ||
      !atonsAdded ||
      (onlyFaultedAtons === readMapSettings("atonOnlyFaulted") &&
        onlyRemoteAtons === readMapSettings("atonOnlyRemote"))
    )
      return;
    setOnlyFaultedAtons(readMapSettings("atonOnlyFaulted"));
    setOnlyRemoteAtons(readMapSettings("atonOnlyRemote"));
    addAtoNsToMap(mapRef.current, atons?.results ?? []);
  }, [
    mapCreated,
    settingsChanged,
    atonsAdded,
    onlyFaultedAtons,
    onlyRemoteAtons,
    atons,
  ]);

  // Update marine survey layer if settings are changed
  useEffect(() => {
    if (!mapRef.current || surveyOutlines === readMapSettings("surveyOutlines"))
      return;
    setSurveyOutlines(readMapSettings("surveyOutlines"));
    addMeasurementsToMap(mapRef.current, measurements?.results ?? []);
  }, [measurements?.results, settingsChanged, surveyOutlines]);

  return (
    <div style={{ overflow: "hidden", width: "100%", height: "100%" }}>
      <Grid
        container
        className={classes.mapContainer}
        style={{
          display:
            mapType === "3d" && cesiumState === "ready" ? "none" : "flex",
        }}
      >
        {focusedPort && (
          <MapPortCallsDrawer
            map={mapRef.current}
            port={focusedPort}
            parentWidth={containerWidth}
            parentHeight={containerHeight}
          />
        )}
        {mapContainer}

        {mapRef.current && (
          <MapDrawer
            open={mapDrawerOpen}
            onClose={() => handleDrawerToggle(false)}
            pinned={pinned}
            onPin={handlePin}
            map={mapRef.current}
          />
        )}
        <PortDialog
          open={activeAction === "addPort"}
          onClose={() => setActiveAction(undefined)}
          onActionDone={() => getPorts()}
          mainMap={mapRef.current}
        />
        <If
          condition={activeAction === "addBerth"}
          render={() => (
            <BerthDialog
              open={activeAction === "addBerth"}
              activeAction="add"
              onClose={() => setActiveAction(undefined)}
              onAdd={(berth) => {
                addBerthMutation(berth);
              }}
              portId={focusedPort?.id}
            />
          )}
        />
        <If
          condition={activeAction === "addPortService"}
          render={() => (
            <ServiceDialog
              activeAction="add"
              onClose={() => setActiveAction(undefined)}
              onSuccess={refetchPortServices}
              portId={focusedPort?.id}
            />
          )}
        />
        <EventDialog
          open={activeAction === "addEvent"}
          onClose={() => setActiveAction(undefined)}
          onActionDone={() => getEvents()}
          mainMap={mapRef.current}
        />
        <If
          condition={activeAction === "addPortArrivalZone"}
          render={() => (
            <ArrivalZoneDialog
              onClose={() => setActiveAction(undefined)}
              portId={focusedPort?.id}
              activeAction="add"
              onSuccess={refectArrivalZonesForPort}
            />
          )}
        />
        <DamDialog
          open={activeAction === "addDam"}
          activeAction="add"
          onAdd={(d, location) => {
            if (!d.maintainerOrganizationId) return;
            addDamMutation({
              ...d,
              location,
              mapAreaIds: d.mapAreaIds ?? undefined,
              maintainerOrganizationId: d.maintainerOrganizationId,
            });
          }}
          onClose={() => setActiveAction(undefined)}
        />
        <FerryDialog
          open={activeAction === "addFerry"}
          activeAction="add"
          onAdd={(d, location) => {
            if (!d.maintainerOrganizationId) return;
            addFerryMutation({
              ...d,
              location,
              mapAreaIds: d.mapAreaIds ?? undefined,
              maintainerOrganizationId: d.maintainerOrganizationId,
            });
          }}
          onClose={() => setActiveAction(undefined)}
        />
      </Grid>

      {mapRef.current && SeaSurveyRead && (
        <Grid
          container
          className={classes.mapContainer}
          style={{
            display: mapType === "2d" ? "none" : "flex",
            position: "relative",
          }}
        >
          <CesiumMap
            map={mapRef.current}
            measurements={measurements?.results}
          />
        </Grid>
      )}
    </div>
  );
};

export default MapPage;
